<template>
  <div class="concern-history">
    <div class="concern-history__card">
      <header class="concern-history__header">
        <div class="concern-history__heading">
          <h3>Historique des analyses</h3>
          <p>Toutes les périodes analysées, propositions retenues et rejetées</p>
        </div>
        <button
          type="button"
          class="concern-history__close"
          aria-label="Fermer l’historique"
          @click="$emit('close')"
        >
          <i class="fas fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="concern-history__filters">
        <label class="concern-history__field">
          <span>Domaine</span>
          <select v-model="selectedDomainUuid">
            <option value="">Tous les domaines</option>
            <option v-for="domain in domains" :key="domain.uuid" :value="domain.uuid">
              {{ domain.label }}
            </option>
          </select>
        </label>
        <p
          v-if="operationMessage"
          class="concern-history__message"
          :class="{ 'concern-history__message--error': operationError }"
        >
          {{ operationMessage }}
        </p>
      </div>

      <div class="concern-history__body">
        <aside class="concern-history__periods" aria-label="Périodes analysées">
          <p class="concern-history__periods-title">Période analysée</p>
          <ul v-if="visiblePeriods.length">
            <li v-for="period in visiblePeriods" :key="period.key">
              <button
                type="button"
                class="concern-history__period"
                :class="{ 'concern-history__period--active': period.key === activePeriodKey }"
                @click="activePeriodKey = period.key"
              >
                <span>{{ period.label }}</span>
                <small>{{ period.rows.length }}</small>
              </button>
            </li>
          </ul>
          <p v-else class="concern-history__empty">Aucune analyse pour ce domaine.</p>
        </aside>

        <section class="concern-history__pane">
          <template v-if="activePeriod">
            <header class="concern-history__pane-header">
              <h4>{{ activePeriod.label }}</h4>
              <p>Période analysée : {{ activePeriod.rangeLabel }}</p>
            </header>
            <EmployeeConcernTable
              :rows="activePeriod.rows"
              :operation-running="operationRunning"
              empty-label="Aucune analyse sur cette période."
              @deepen="concern => $emit('deepen', concern)"
            />
          </template>
          <p v-else class="concern-history__empty concern-history__empty--pane">
            Aucune période à afficher.
          </p>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import EmployeeConcernTable from '@/components/dashboard/EmployeeConcernTable.vue'

export default {
  name: 'EmployeeConcernHistoryModal',
  components: { EmployeeConcernTable },
  props: {
    /** Periods of shape { key, label, rangeLabel, rows: [{ key, run, concern }] }, most recent first. */
    periods: { type: Array, default: () => [] },
    domains: { type: Array, default: () => [] },
    initialPeriodKey: { type: String, default: '' },
    operationRunning: { type: Boolean, default: false },
    operationMessage: { type: String, default: '' },
    operationError: { type: Boolean, default: false }
  },
  data () {
    return {
      selectedDomainUuid: '',
      activePeriodKey: this.initialPeriodKey
    }
  },
  computed: {
    visiblePeriods () {
      return this.periods
        .map(period => ({
          ...period,
          rows: this.selectedDomainUuid
            ? period.rows.filter(row => row.run.domain && row.run.domain.uuid === this.selectedDomainUuid)
            : period.rows
        }))
        .filter(period => period.rows.length > 0)
    },
    activePeriod () {
      return this.visiblePeriods.find(period => period.key === this.activePeriodKey) || null
    }
  },
  watch: {
    visiblePeriods: {
      immediate: true,
      handler (periods) {
        if (periods.some(period => period.key === this.activePeriodKey)) return
        this.activePeriodKey = periods.length ? periods[0].key : ''
      }
    }
  }
}
</script>

<style scoped>
.concern-history {
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}

.concern-history__card {
  display: flex;
  width: 100%;
  max-height: calc(100vh - 5rem);
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 26px 48px -32px rgba(15, 23, 42, 0.35);
}

.concern-history__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.5rem 1.75rem 1rem;
  background: linear-gradient(135deg, rgba(58, 123, 213, 0.1), rgba(58, 123, 213, 0.02));
}

.concern-history__heading h3 {
  margin: 0;
  color: #111827;
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.25;
}

.concern-history__heading p {
  margin: 0.3rem 0 0;
  color: #2563eb;
  font-size: 0.8rem;
}

.concern-history__close {
  display: inline-flex;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  align-items: center;
  justify-content: center;
  border: 1px solid #2563eb;
  border-radius: 0.55rem;
  background: #fff;
  color: #2563eb;
  cursor: pointer;
  transition: background 0.2s ease;
}

.concern-history__close:hover {
  background: #eff6ff;
}

.concern-history__filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem;
  padding: 1rem 1.75rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.concern-history__field {
  display: flex;
  min-width: 16rem;
  flex-direction: column;
  gap: 0.35rem;
  color: #111827;
  font-size: 0.875rem;
}

.concern-history__field span {
  color: #4b5563;
  font-size: 0.75rem;
  font-weight: 700;
}

.concern-history__field select {
  height: 2.4rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.55rem;
  background: #fff;
  padding: 0 0.75rem;
  color: #111827;
}

.concern-history__message {
  margin: 0 0 0.6rem;
  color: #2563eb;
  font-size: 0.75rem;
}

.concern-history__message--error {
  color: #b91c1c;
}

.concern-history__body {
  display: flex;
  min-height: 0;
  flex: 1 1 auto;
}

.concern-history__periods {
  width: 13.5rem;
  flex: 0 0 13.5rem;
  overflow-y: auto;
  border-right: 1px solid #e5e7eb;
  padding: 1rem 0.75rem;
}

.concern-history__periods-title {
  margin: 0 0 0.6rem;
  padding: 0 0.75rem;
  color: #4b5563;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.concern-history__periods ul {
  margin: 0;
  padding: 0;
  list-style: none;
}

.concern-history__period {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  border: 0;
  border-radius: 0.55rem;
  background: none;
  padding: 0.6rem 0.75rem;
  color: #111827;
  font-size: 0.875rem;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease;
}

.concern-history__period small {
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 400;
}

.concern-history__period:hover {
  background: #f3f4f6;
}

.concern-history__period--active,
.concern-history__period--active:hover {
  background: #eff6ff;
  color: #1d4ed8;
}

.concern-history__pane {
  min-width: 0;
  flex: 1 1 auto;
  overflow-y: auto;
}

.concern-history__pane-header {
  padding: 1rem 1.25rem 0.75rem;
  border-bottom: 1px solid #e5e7eb;
}

.concern-history__pane-header h4 {
  margin: 0;
  color: #111827;
  font-size: 1rem;
  font-weight: 600;
}

.concern-history__pane-header p {
  margin: 0.2rem 0 0;
  color: #4b5563;
  font-size: 0.75rem;
}

.concern-history__empty {
  margin: 0;
  padding: 1rem 0.75rem;
  color: #6b7280;
  font-size: 0.8rem;
}

.concern-history__empty--pane {
  padding: 2.5rem 1.5rem;
  text-align: center;
}

@media (max-width: 900px) {
  .concern-history__body {
    flex-direction: column;
  }

  .concern-history__periods {
    width: auto;
    flex: 0 0 auto;
    max-height: 11rem;
    border-right: 0;
    border-bottom: 1px solid #e5e7eb;
  }
}
</style>
